/**
 * Holdings page - manage positions
 */
import { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  useHoldings,
  useAccounts,
  usePortfolios,
  useTags,
  useCreateHolding,
  useUpdateHolding,
  useDeleteHolding,
  useSetHoldingTags,
  useBulkUpdateHoldingTags,
  useBulkUpdateHoldingPortfolio,
  useBulkDeleteHoldings,
  useTheses,
  useHoldingTheses,
  useSetHoldingTheses,
  useSortableTable,
} from '../hooks'
import { useEarningsCalendar } from '../hooks/useEarnings'
import { usePortfolioFilter } from '../contexts'
import { Button, EmptyState, Input, Modal, LoadingCard, SortableHeader, EarningsBadge } from '../components/common'
import { BulkActionBar } from '../components/BulkActionBar'
import type { Holding, HoldingCreate, HoldingUpdate, Tag } from '../types'
import type { BulkTagAction } from '../api/holdings'

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value)
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
}

export function HoldingsPage() {
  // Global portfolio filter (from navbar selector)
  const { selectedPortfolioId } = usePortfolioFilter()

  // Local filter state (account and tag only - portfolio is global now)
  const [selectedAccount, setSelectedAccount] = useState<string>('')
  const [selectedTagFilter, setSelectedTagFilter] = useState<string>('')

  // Selection state (for bulk operations)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingHolding, setEditingHolding] = useState<Holding | null>(null)
  const [error, setError] = useState('')

  // Form state
  const [formData, setFormData] = useState<HoldingCreate>({
    account_id: '',
    symbol: '',
    shares: 0,
    cost_basis: 0,
    portfolio_id: '',  // User must select a portfolio
  })
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedThesisIds, setSelectedThesisIds] = useState<string[]>([])

  // Queries - filtered by global portfolio selection
  const { data: holdings, isLoading: holdingsLoading } = useHoldings({
    accountId: selectedAccount || undefined,
    portfolioId: selectedPortfolioId || undefined,
    tagIds: selectedTagFilter ? [selectedTagFilter] : undefined,
  })

  // Sortable table
  const { sortedData: sortedHoldings, requestSort, getSortDirection } = useSortableTable(
    holdings,
    { key: 'symbol', direction: 'asc' }
  )

  const { data: accounts, isLoading: accountsLoading } = useAccounts()
  const { data: portfolios, isLoading: portfoliosLoading } = usePortfolios()
  const { data: tags, isLoading: tagsLoading } = useTags()
  const { data: theses, isLoading: thesesLoading } = useTheses({ status: 'active' })

  // Fetch earnings calendar for badges (only need 30 days for urgency display)
  const { data: earningsCalendar } = useEarningsCalendar(30)

  // Create earnings lookup by symbol
  const earningsMap = useMemo(() => {
    const map = new Map<string, { days_until: number; report_date: string }>()
    if (earningsCalendar?.entries) {
      for (const entry of earningsCalendar.entries) {
        // Only store the nearest earnings date per symbol
        if (!map.has(entry.symbol)) {
          map.set(entry.symbol, { days_until: entry.days_until, report_date: entry.report_date })
        }
      }
    }
    return map
  }, [earningsCalendar])

  // Fetch thesis associations for the holding being edited
  const { data: holdingTheses } = useHoldingTheses(editingHolding?.holding_id ?? '')

  // Mutations
  const createHolding = useCreateHolding()
  const updateHolding = useUpdateHolding()
  const deleteHolding = useDeleteHolding()
  const setHoldingTags = useSetHoldingTags()
  const bulkUpdateTags = useBulkUpdateHoldingTags()
  const bulkUpdatePortfolio = useBulkUpdateHoldingPortfolio()
  const bulkDeleteHoldings = useBulkDeleteHoldings()
  const setHoldingTheses = useSetHoldingTheses()

  const isLoading = holdingsLoading || accountsLoading || portfoliosLoading || tagsLoading || thesesLoading

  // Sort portfolios alphabetically
  const sortedPortfolios = useMemo(() => {
    return [...(portfolios || [])].sort((a, b) => a.name.localeCompare(b.name))
  }, [portfolios])

  // Create tag lookup map
  const tagMap = useMemo(() => {
    return new Map((tags || []).map(t => [t.tag_id, t]))
  }, [tags])

  // Sync thesis associations when editing a holding
  useEffect(() => {
    if (editingHolding && holdingTheses) {
      setSelectedThesisIds(holdingTheses.map(t => t.id))
    }
  }, [editingHolding, holdingTheses])

  const openCreateModal = () => {
    setEditingHolding(null)
    setFormData({
      account_id: selectedAccount || accounts?.[0]?.account_id || '',
      symbol: '',
      shares: 0,
      cost_basis: 0,
      portfolio_id: selectedPortfolioId || portfolios?.[0]?.portfolio_id || '',
    })
    setSelectedTags([])
    setSelectedThesisIds([])
    setError('')
    setIsModalOpen(true)
  }

  const openEditModal = (holding: Holding) => {
    setEditingHolding(holding)
    setFormData({
      account_id: holding.account_id,
      symbol: holding.symbol,
      shares: holding.shares,
      cost_basis: holding.cost_basis,
      manual_value: holding.manual_value,
      notes: holding.notes,
      portfolio_id: holding.portfolio_id,
    })
    setSelectedTags(holding.tags || [])
    setError('')
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      if (editingHolding) {
        const update: HoldingUpdate = {
          shares: formData.shares,
          cost_basis: formData.cost_basis,
          manual_value: formData.manual_value,
          notes: formData.notes,
          portfolio_id: formData.portfolio_id,
        }
        await updateHolding.mutateAsync({
          holdingId: editingHolding.holding_id,
          data: update,
        })
        // Update tags if they changed
        const tagsChanged =
          JSON.stringify([...selectedTags].sort()) !==
          JSON.stringify([...(editingHolding.tags || [])].sort())
        if (tagsChanged) {
          await setHoldingTags.mutateAsync({
            holdingId: editingHolding.holding_id,
            tagIds: selectedTags,
          })
        }
        // Update thesis associations if they changed
        const currentThesisIds = (holdingTheses || []).map(t => t.id)
        const thesesChanged =
          JSON.stringify([...selectedThesisIds].sort()) !==
          JSON.stringify([...currentThesisIds].sort())
        if (thesesChanged) {
          await setHoldingTheses.mutateAsync({
            holdingId: editingHolding.holding_id,
            data: { thesis_ids: selectedThesisIds },
          })
        }
      } else {
        const newHolding = await createHolding.mutateAsync(formData)
        // Set tags on the new holding
        if (selectedTags.length > 0) {
          await setHoldingTags.mutateAsync({
            holdingId: newHolding.holding_id,
            tagIds: selectedTags,
          })
        }
        // Set thesis associations on the new holding
        if (selectedThesisIds.length > 0) {
          await setHoldingTheses.mutateAsync({
            holdingId: newHolding.holding_id,
            data: { thesis_ids: selectedThesisIds },
          })
        }
      }
      setIsModalOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    }
  }

  const handleDelete = async (holdingId: string) => {
    if (!confirm('Are you sure you want to delete this holding?')) return

    try {
      await deleteHolding.mutateAsync(holdingId)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete holding')
    }
  }

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  const toggleThesis = (thesisId: string) => {
    setSelectedThesisIds(prev =>
      prev.includes(thesisId) ? prev.filter(id => id !== thesisId) : [...prev, thesisId]
    )
  }

  // Selection handlers
  const toggleSelection = (holdingId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(holdingId)) {
        next.delete(holdingId)
      } else {
        next.add(holdingId)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (!holdings) return
    if (selectedIds.size === holdings.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(holdings.map(h => h.holding_id)))
    }
  }

  const clearSelection = () => setSelectedIds(new Set())

  // Bulk action handlers
  const handleBulkTags = async (action: BulkTagAction, tagIds: string[]) => {
    await bulkUpdateTags.mutateAsync({
      holdingIds: Array.from(selectedIds),
      action,
      tagIds,
    })
  }

  const handleBulkPortfolio = async (portfolioId: string) => {
    await bulkUpdatePortfolio.mutateAsync({
      holdingIds: Array.from(selectedIds),
      portfolioId,
    })
  }

  const handleBulkDelete = async () => {
    await bulkDeleteHoldings.mutateAsync(Array.from(selectedIds))
  }

  const isBulkLoading = bulkUpdateTags.isPending || bulkUpdatePortfolio.isPending || bulkDeleteHoldings.isPending

  if (isLoading) {
    return <LoadingCard />
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Holdings</h1>
        <div className="flex items-center space-x-4">
          {/* Account filter */}
          <select
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            <option value="">All Accounts</option>
            {accounts?.map((account) => (
              <option key={account.account_id} value={account.account_id}>
                {account.name}
              </option>
            ))}
          </select>

          {/* Tag filter */}
          <select
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            value={selectedTagFilter}
            onChange={(e) => setSelectedTagFilter(e.target.value)}
          >
            <option value="">All Tags</option>
            {tags?.map((tag) => (
              <option key={tag.tag_id} value={tag.tag_id}>
                {tag.name}
              </option>
            ))}
          </select>

          <Button onClick={openCreateModal} disabled={!accounts?.length}>
            Add Holding
          </Button>
        </div>
      </div>

      {!accounts?.length ? (
        <EmptyState
          icon="accounts"
          title="No Accounts Configured"
          description="Create an account in Settings to start tracking your holdings. Accounts represent your brokerage accounts or tax buckets."
          action={{
            label: 'Go to Settings',
            onClick: () => window.location.href = '/settings',
          }}
        />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={sortedHoldings.length > 0 && selectedIds.size === sortedHoldings.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <SortableHeader
                  label="Symbol"
                  direction={getSortDirection('symbol')}
                  onSort={() => requestSort('symbol')}
                />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Portfolio
                </th>
                <SortableHeader
                  label="Shares"
                  direction={getSortDirection('shares')}
                  onSort={() => requestSort('shares')}
                  align="right"
                />
                <SortableHeader
                  label="Cost Basis"
                  direction={getSortDirection('cost_basis')}
                  onSort={() => requestSort('cost_basis')}
                  align="right"
                />
                <SortableHeader
                  label="Current Value"
                  direction={getSortDirection('current_value')}
                  onSort={() => requestSort('current_value')}
                  align="right"
                />
                <SortableHeader
                  label="Gain/Loss"
                  direction={getSortDirection('gain_loss')}
                  onSort={() => requestSort('gain_loss')}
                  align="right"
                />
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedHoldings.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                    No holdings found. Add your first holding to get started.
                  </td>
                </tr>
              ) : (
                sortedHoldings.map((holding) => {
                  const account = accounts?.find(a => a.account_id === holding.account_id)
                  const portfolio = portfolios?.find(p => p.portfolio_id === holding.portfolio_id)
                  const gainLossColor = (holding.gain_loss ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  const holdingTags = holding.tags?.map(tagId => tagMap.get(tagId)).filter(Boolean) as Tag[]
                  const isSelected = selectedIds.has(holding.holding_id)
                  const earnings = earningsMap.get(holding.symbol)

                  return (
                    <tr
                      key={holding.holding_id}
                      className={`hover:bg-gray-50 ${isSelected ? 'bg-primary-50' : ''}`}
                    >
                      <td className="px-4 py-4 w-10">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={isSelected}
                          onChange={() => toggleSelection(holding.holding_id)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {holding.symbol}
                          </span>
                          {earnings && (
                            <EarningsBadge
                              daysUntil={earnings.days_until}
                              reportDate={earnings.report_date}
                            />
                          )}
                        </div>
                        {holdingTags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {holdingTags.map(tag => (
                              <span
                                key={tag.tag_id}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                style={{
                                  backgroundColor: `${tag.color || '#6B7280'}20`,
                                  color: tag.color || '#6B7280',
                                }}
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        )}
                        {holding.notes && (
                          <p className="text-xs text-gray-500 truncate max-w-xs mt-1">
                            {holding.notes}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {account?.name || holding.account_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {portfolio && (
                          <span
                            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                            style={{
                              backgroundColor: `${portfolio.color || '#6B7280'}15`,
                              color: portfolio.color || '#6B7280',
                              border: `1px solid ${portfolio.color || '#6B7280'}30`,
                            }}
                          >
                            {portfolio.name}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {holding.shares.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(holding.cost_basis)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {holding.manual_value
                          ? formatCurrency(holding.manual_value) + ' (manual)'
                          : formatCurrency(holding.current_value)
                        }
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${gainLossColor}`}>
                        {formatCurrency(holding.gain_loss)}
                        <br />
                        <span className="text-xs">{formatPercent(holding.gain_loss_pct)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right space-x-3">
                        <Link
                          to={`/what-if/buy?symbol=${holding.symbol}`}
                          className="text-blue-600 hover:text-blue-900"
                          title="Simulate buying more shares"
                        >
                          Simulate
                        </Link>
                        <Link
                          to={`/what-if/position?symbol=${holding.symbol}`}
                          className="text-purple-600 hover:text-purple-900"
                          title="Calculate position size"
                        >
                          Size
                        </Link>
                        <button
                          className="text-primary-600 hover:text-primary-900"
                          onClick={() => openEditModal(holding)}
                        >
                          Edit
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900"
                          onClick={() => handleDelete(holding.holding_id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Holding Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingHolding ? 'Edit Holding' : 'Add Holding'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingHolding && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account
                </label>
                <select
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={formData.account_id}
                  onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                  required
                >
                  <option value="">Select account...</option>
                  {accounts?.map((account) => (
                    <option key={account.account_id} value={account.account_id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                label="Symbol"
                value={formData.symbol}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                placeholder="e.g., SPY, IONQ"
                required
              />
            </>
          )}

          {editingHolding && (
            <div className="text-sm text-gray-500">
              <strong>{editingHolding.symbol}</strong> in {accounts?.find(a => a.account_id === editingHolding.account_id)?.name}
            </div>
          )}

          {/* Portfolio selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Portfolio
            </label>
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={formData.portfolio_id}
              onChange={(e) => setFormData({ ...formData, portfolio_id: e.target.value })}
            >
              {sortedPortfolios.map((portfolio) => (
                <option key={portfolio.portfolio_id} value={portfolio.portfolio_id}>
                  {portfolio.name}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Shares"
            type="number"
            step="any"
            min="0"
            value={formData.shares}
            onChange={(e) => setFormData({ ...formData, shares: parseFloat(e.target.value) || 0 })}
            required
          />

          <Input
            label="Cost Basis (total)"
            type="number"
            step="0.01"
            min="0"
            value={formData.cost_basis}
            onChange={(e) => setFormData({ ...formData, cost_basis: parseFloat(e.target.value) || 0 })}
            helperText={formData.shares > 0 ? `$${(formData.cost_basis / formData.shares).toFixed(2)} per share` : ''}
            required
          />

          <Input
            label="Manual Value (optional)"
            type="number"
            step="0.01"
            min="0"
            value={formData.manual_value || ''}
            onChange={(e) => setFormData({ ...formData, manual_value: parseFloat(e.target.value) || undefined })}
            helperText="Override market value for physical assets"
          />

          <Input
            label="Notes (optional)"
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value || undefined })}
            placeholder="e.g., Core position, tax-loss harvest candidate"
          />

          {/* Tags multi-select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            {tags?.length === 0 ? (
              <p className="text-sm text-gray-500">
                No tags created yet.{' '}
                <a href="/tags" className="text-primary-600 hover:underline">Create some tags</a>
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags?.map((tag) => {
                  const isSelected = selectedTags.includes(tag.tag_id)
                  return (
                    <button
                      key={tag.tag_id}
                      type="button"
                      onClick={() => toggleTag(tag.tag_id)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'ring-2 ring-offset-1'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      style={{
                        backgroundColor: `${tag.color || '#6B7280'}20`,
                        color: tag.color || '#6B7280',
                        borderColor: tag.color || '#6B7280',
                        ...(isSelected ? { ringColor: tag.color || '#6B7280' } : {}),
                      }}
                    >
                      {tag.name}
                      {isSelected && (
                        <svg className="ml-1 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Theses multi-select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Theses
            </label>
            {theses?.length === 0 ? (
              <p className="text-sm text-gray-500">
                No theses created yet.{' '}
                <a href="/strategy/theses" className="text-primary-600 hover:underline">Create some theses</a>
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {theses?.map((thesis) => {
                  const isSelected = selectedThesisIds.includes(thesis.id)
                  return (
                    <button
                      key={thesis.id}
                      type="button"
                      onClick={() => toggleThesis(thesis.id)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-500 ring-offset-1'
                          : 'bg-gray-100 text-gray-600 opacity-60 hover:opacity-100 hover:bg-gray-200'
                      }`}
                    >
                      {thesis.name}
                      {thesis.target_allocation_pct !== null && thesis.target_allocation_pct !== undefined && (
                        <span className="ml-1 text-xs opacity-75">
                          ({thesis.target_allocation_pct}%)
                        </span>
                      )}
                      {isSelected && (
                        <svg className="ml-1 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createHolding.isPending || updateHolding.isPending || setHoldingTags.isPending || setHoldingTheses.isPending}
            >
              {editingHolding ? 'Save Changes' : 'Add Holding'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selectedIds.size}
        onClearSelection={clearSelection}
        portfolios={portfolios}
        onBulkPortfolio={handleBulkPortfolio}
        tags={tags}
        onBulkTags={handleBulkTags}
        onBulkDelete={handleBulkDelete}
        isLoading={isBulkLoading}
      />
    </div>
  )
}
